import { ExpandLess } from "@mui/icons-material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Box, Collapse, Typography } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { DateTime } from "luxon";
import { type FC, type MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDraftMode } from "../../../contexts/DraftModeContext";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../../contexts/RequestsContext";
import { useWeekViewContext } from "../../../contexts/WeekView";
import { useRequestOrigins } from "../../../hooks/useRequestOrigins";
import { getShiftTypeFromStartTime } from "../../../utils/dateUtils";
import type { OpenShiftRequest, RequestOriginGroup, RequestOrigins, ShiftRequest } from "../../../utils/models";
import { RequestOriginsStatus, ShiftType } from "../../../utils/models";
import { countByOriginGroup, matchesOriginGroups } from "../../../utils/requestOrigins";
import { requestTakesAnyRole } from "../../../utils/roles";
import { GapsOpenShifts } from "../../staffing/mobileGaps/openShifts/GapsOpenShifts";
import { SystemFlatSwitch } from "../../UIToolkit/Clickable/SystemFlatSwitch";
import type { ShiftTypeFilterValue } from "../../UIToolkit/molecules/ShiftTypeFilterChips";
import { useDepartmentReuqestAgentIds } from "../hooks/useAgentHistory";
import type { OriginFilterControl } from "../RequestOrigin/requestOriginMeta";
import type { OutreachCoachMark } from "../Outreach/RequestOutreachEntry";
import { useOutreachCoachMark } from "../Outreach/useOutreachCoachMark";
import { RequestItem } from "./RequestItem";
import { RequestsTabFilters } from "./RequestsTabFilters";
import { useRequestItemActions } from "./useRequestItemActions";
import { expandRoleGroupsToAcceptedRoles, matchesResponseFilter, RequestFilter } from "./utils";

/**
 * Which layout the Requests screen draws. `classic` is the screen as it has always been; `gaps` is the gaps-first mobile
 * view's Open Shifts (P3-12) — the same requests, narrowed by the same filters, drawn by day with the answers inline.
 */
export type RequestsTabVariant = "classic" | "gaps";

type RequestsTabProps = {
	date?: DateTime;
	requestId?: string | null;
	/** Absent = `classic`, the existing screen, unchanged. `gaps` does not deep-link: `requestId` is classic's. */
	variant?: RequestsTabVariant;
};

type RequestSectionProps = {
	title: string;
	requests: ShiftRequest[];
	isExpanded: boolean;
	onToggle: () => void;
	onDelete: (event: MouseEvent<HTMLElement>, request: ShiftRequest) => void;
	onBroadcast: (event: MouseEvent<HTMLElement>, request: ShiftRequest) => void;
	onEditNeededNurses: (event: MouseEvent<HTMLElement>, request: OpenShiftRequest) => void;
	onShowAll: (request: ShiftRequest) => void;
	onTimeChange: (request: ShiftRequest, startTime: DateTime, endTime: DateTime) => Promise<void>;
	requestsAgentIds?: string[];
	highlightedRequestId: string | null;
	originsByRequestId: ReadonlyMap<string, RequestOrigins>;
	/** The first-open highlight and the one card that carries it; `null` once it's done. */
	outreachCoachMark: { requestId: string; mark: NonNullable<OutreachCoachMark> } | null;
};

/** Top to bottom, as the tab stacks them. */
const SECTION_ORDER = ["currentWeek", "nextWeek", "future"] as const;

const RequestSection: FC<RequestSectionProps> = ({
	title,
	requests,
	isExpanded,
	onToggle,
	onDelete,
	onBroadcast,
	onEditNeededNurses,
	onShowAll,
	onTimeChange,
	requestsAgentIds,
	highlightedRequestId,
	originsByRequestId,
	outreachCoachMark,
}) => {
	if (requests.length === 0) return null;

	return (
		<Box sx={{ mb: 2 }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					padding: 2,
					backgroundColor: "background.paper",
					borderRadius: 1,
					cursor: "pointer",
					mb: 1,
					"&:hover": { backgroundColor: "action.hover" },
				}}
				data-testid={`request-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
				onClick={onToggle}
			>
				<Typography variant="h6" sx={(theme) => ({ fontWeight: 600, color: theme.palette.primary.main })}>
					{title} ({requests.length})
				</Typography>
				{isExpanded ? (
					<ExpandLess sx={(theme) => ({ color: theme.palette.primary.main })} />
				) : (
					<ExpandMore sx={(theme) => ({ color: theme.palette.primary.main })} />
				)}
			</Box>
			<Collapse in={isExpanded}>
				<AnimatePresence mode="popLayout">
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 1,
						}}
					>
						{requests.map((request, index) => (
							<motion.div
								key={request.id}
								id={`request-${request.id}`}
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.3, delay: index * 0.05, ease: "easeOut" }}
							>
								<RequestItem
									request={request}
									onDelete={onDelete}
									onBroadcast={onBroadcast}
									onEditNeededNurses={onEditNeededNurses}
									onShowAll={onShowAll}
									onTimeChange={onTimeChange}
									hasCallOff={requestsAgentIds?.includes(request.id)}
									highlightedRequestId={highlightedRequestId}
									origins={originsByRequestId.get(request.id) ?? null}
									outreachCoachMark={outreachCoachMark?.requestId === request.id ? outreachCoachMark.mark : null}
								/>
							</motion.div>
						))}
					</Box>
				</AnimatePresence>
			</Collapse>
		</Box>
	);
};

export const RequestsTab: FC<RequestsTabProps> = ({ date, requestId, variant }) => {
	const { t } = useTranslation();
	const { weekViewData } = useWeekViewContext();
	const { staffRequests } = useRequestsContext();
	const { isDraftMode, isRequestLocallyApproved } = useDraftMode();
	const [selectedFilter, setSelectedFilter] = useState<RequestFilter>(RequestFilter.All);
	const [selectedRoleGroups, setSelectedRoleGroups] = useState<string[]>([]);
	const [selectedJobFilters, setSelectedJobFilters] = useState<string[]>([]);
	const [selectedShiftType, setSelectedShiftType] = useState<ShiftTypeFilterValue>(ShiftType.All);
	const [selectedOriginGroups, setSelectedOriginGroups] = useState<RequestOriginGroup[]>([]);
	const { chosenDepartment } = useHospitalData();
	const { data: requestsAgentIds } = useDepartmentReuqestAgentIds();
	const { handlers, dialogs } = useRequestItemActions();

	const sortRequests = useCallback((a: ShiftRequest, b: ShiftRequest) => {
		const dateA = a.message?.date;
		const dateB = b.message?.date;

		// First sort by date
		if (dateA !== dateB) return dateA < dateB ? -1 : 1;

		// Sort by number of accepted nurses (higher number first)
		const acceptedNursesA = a.message?.accepted_nurses?.length || 0;
		const acceptedNursesB = b.message?.accepted_nurses?.length || 0;

		if (acceptedNursesB > acceptedNursesA) return 1;
		if (acceptedNursesB < acceptedNursesA) return -1;

		// Sort by ID as final fallback for consistent ordering
		if (a.id && b.id) {
			if (a.id < b.id) return -1;
			if (a.id > b.id) return 1;
		}

		return 0;
	}, []);

	const baseRequests = useMemo<ShiftRequest[]>(() => (date ? weekViewData[date.weekday % 7].requests : staffRequests), [weekViewData, staffRequests, date]);

	const matchesShiftTypeFilter = useCallback(
		(request: ShiftRequest): boolean => {
			if (selectedShiftType === ShiftType.All) return true;
			return getShiftTypeFromStartTime(request.message.shift_start) === selectedShiftType;
		},
		[selectedShiftType],
	);

	const acceptedRoles = useMemo(
		() => expandRoleGroupsToAcceptedRoles(selectedRoleGroups, chosenDepartment.role_groups),
		[selectedRoleGroups, chosenDepartment.role_groups],
	);

	const matchesRoleOrJobFilter = useCallback(
		(request: ShiftRequest): boolean => {
			if (selectedJobFilters.length > 0 && (!request.job_id || !selectedJobFilters.includes(request.job_id))) return false;
			if (selectedRoleGroups.length === 0) return true;
			return requestTakesAnyRole(request.message.roles, acceptedRoles);
		},
		[selectedJobFilters, selectedRoleGroups, acceptedRoles],
	);

	const origins = useRequestOrigins(chosenDepartment.department_id, baseRequests);
	const hasOrigins = origins.status === RequestOriginsStatus.Ready;

	// The draft-mode, job / role-group and day / night narrowing — the scope the Origin filter counts in.
	const roleAndTimeScoped = useMemo<ShiftRequest[]>(() => {
		// Filter out locally approved/declined requests in draft mode
		const visible = isDraftMode ? baseRequests.filter((request) => !isRequestLocallyApproved(request.id)) : baseRequests;
		return visible.filter((request) => matchesRoleOrJobFilter(request) && matchesShiftTypeFilter(request));
	}, [baseRequests, isDraftMode, isRequestLocallyApproved, matchesRoleOrJobFilter, matchesShiftTypeFilter]);

	// Everything but the response switch. Split from the switch so the gaps layout can count what each switch option
	// would list.
	const scopedRequests = useMemo<ShiftRequest[]>(
		() =>
			hasOrigins ? roleAndTimeScoped.filter((request) => matchesOriginGroups(origins.byRequestId.get(request.id), selectedOriginGroups)) : roleAndTimeScoped,
		[hasOrigins, roleAndTimeScoped, origins.byRequestId, selectedOriginGroups],
	);

	const originFilter = useMemo<OriginFilterControl | null>(
		() =>
			hasOrigins
				? {
						selected: selectedOriginGroups,
						onChange: setSelectedOriginGroups,
						// Counted in the list's own scope — role, day / night and the response switch — so a count is what
						// choosing that group would show.
						counts: countByOriginGroup(
							roleAndTimeScoped.filter((request) => matchesResponseFilter(request, selectedFilter)).map((request) => request.id),
							origins.byRequestId,
						),
					}
				: null,
		[hasOrigins, selectedOriginGroups, roleAndTimeScoped, selectedFilter, origins.byRequestId],
	);

	const filteredRequests = useMemo<ShiftRequest[]>(
		() => scopedRequests.filter((request) => matchesResponseFilter(request, selectedFilter)),
		[scopedRequests, selectedFilter],
	);

	const sortedRequests = useMemo<ShiftRequest[]>(() => [...filteredRequests].sort((a, b) => sortRequests(a, b)), [filteredRequests, sortRequests]);
	const [highlightedRequestId, setHighlightedRequestId] = useState<string | null>(null);

	const groupedRequests = useMemo(() => {
		const now = DateTime.now();
		const currentWeekStart = now.startOf("week");
		const nextWeekStart = currentWeekStart.plus({ weeks: 1 });

		const currentWeek: ShiftRequest[] = [];
		const nextWeek: ShiftRequest[] = [];
		const future: ShiftRequest[] = [];

		for (const request of sortedRequests) {
			const requestWeekStart = request.message.shift_start.startOf("week");

			if (requestWeekStart <= currentWeekStart) {
				currentWeek.push(request);
			} else if (requestWeekStart.equals(nextWeekStart)) {
				nextWeek.push(request);
			} else {
				future.push(request);
			}
		}

		return { currentWeek, nextWeek, future };
	}, [sortedRequests]);

	const [expandedSections, setExpandedSections] = useState({
		currentWeek: true,
		nextWeek: true,
		future: false,
	});

	const toggleSection = (section: keyof typeof expandedSections): void => {
		setExpandedSections((prev) => ({
			...prev,
			[section]: !prev[section],
		}));
	};

	useEffect(() => {
		if (requestId && sortedRequests.length > 0) {
			// Expand the section containing the target request so it renders in the DOM
			if (groupedRequests.future.some((r) => r.id === requestId)) {
				setExpandedSections((prev) => ({ ...prev, future: true }));
			} else if (groupedRequests.nextWeek.some((r) => r.id === requestId)) {
				setExpandedSections((prev) => ({ ...prev, nextWeek: true }));
			} else if (groupedRequests.currentWeek.some((r) => r.id === requestId)) {
				setExpandedSections((prev) => ({ ...prev, currentWeek: true }));
			}

			// Set the highlighted request ID
			setHighlightedRequestId(requestId);

			// Wait for the DOM to update with the request items
			const scrollTimeoutId = setTimeout(() => {
				const element = document.getElementById(`request-${requestId}`);
				if (element) {
					element.scrollIntoView({ behavior: "smooth", block: "center" });
				}
			}, 500);

			// Clear the highlight after 3 seconds
			const highlightTimeoutId = setTimeout(() => {
				setHighlightedRequestId(null);
			}, 3000);

			return () => {
				clearTimeout(scrollTimeoutId);
				clearTimeout(highlightTimeoutId);
			};
		}
	}, [requestId, sortedRequests, groupedRequests]);

	const coachMark = useOutreachCoachMark();
	// The first card the user can see: a collapsed section's cards aren't rendered, so the mark would sit unseen.
	const coachRequestId = SECTION_ORDER.flatMap((section) => (expandedSections[section] ? groupedRequests[section] : [])).at(0)?.id;
	const outreachCoachMark = useMemo(
		() => (coachMark !== null && coachRequestId !== undefined ? { requestId: coachRequestId, mark: coachMark } : null),
		[coachMark, coachRequestId],
	);

	if (variant === "gaps") {
		return (
			<GapsOpenShifts
				requests={scopedRequests}
				responseFilter={selectedFilter}
				onResponseFilterChange={setSelectedFilter}
				roleGroupFilter={{ selected: selectedRoleGroups, onChange: setSelectedRoleGroups }}
				jobFilter={{ selected: selectedJobFilters, onChange: setSelectedJobFilters }}
				shiftTypeFilter={{ selected: selectedShiftType, onChange: setSelectedShiftType }}
				originFilter={originFilter}
				originsByRequestId={origins.byRequestId}
				requestsAgentIds={requestsAgentIds}
			/>
		);
	}

	// width:100% matters for the standalone mobile /requests route: ContentWrapper lays its child out as a row
	// flex item, so without it the tab shrinks to its intrinsic width and leaves half the screen empty.
	return (
		<Box data-testid="requests-tab-root" sx={{ overflow: "hidden", height: "100%", width: "100%", display: "flex", flexDirection: "column" }}>
			<RequestsTabFilters
				selectedRoleGroups={selectedRoleGroups}
				onSelectedRoleGroupsChanged={setSelectedRoleGroups}
				selectedJobIds={selectedJobFilters}
				onSelectedJobIdsChanged={setSelectedJobFilters}
				selectedShiftType={selectedShiftType}
				onSelectedShiftTypeChanged={setSelectedShiftType}
				originFilter={originFilter}
			/>
			<Box sx={{ display: "flex", justifyContent: "center", flexShrink: 0, px: 2, overflow: "hidden" }}>
				<SystemFlatSwitch
					rootWidth={{ xs: "100%", sm: "fit-content" }}
					rootMaxWidth={{ xs: "100%", sm: "100%" }}
					fontSizeOverrides={{
						xs: "10px",
						xl: "12px",
						xxl: "12px",
					}}
					maxWidthOverrides={{
						xs: "84px",
						xl: "100px",
						xxl: "100px",
					}}
					options={[
						{
							value: RequestFilter.All,
							label: "All",
						},
						{
							value: RequestFilter.WithResponse,
							label: "Answered",
						},
						{
							value: RequestFilter.WithoutResponse,
							label: "Unanswered",
						},
						{
							value: RequestFilter.ReplaceRequest,
							label: "Replace",
						},
					]}
					activeIndexOrValue={selectedFilter}
					onSwitch={(option) => setSelectedFilter(option.value)}
					data-testid="requests-filter"
				/>
			</Box>
			<Box sx={{ overflowX: "hidden", overflowY: "auto", flex: 1, minHeight: 0, marginTop: 1 }}>
				{sortedRequests.length === 0 ? (
					<Box sx={{ minHeight: 220, display: "flex", alignItems: "center", justifyContent: "center", px: 2 }}>
						<Typography sx={{ color: "text.secondary", textAlign: "center" }}>{t("no.requests")}</Typography>
					</Box>
				) : (
					<>
						<RequestSection
							title="Current Week"
							requests={groupedRequests.currentWeek}
							isExpanded={expandedSections.currentWeek}
							onToggle={() => toggleSection("currentWeek")}
							onDelete={handlers.onDelete}
							onBroadcast={handlers.onBroadcast}
							onEditNeededNurses={handlers.onEditNeededNurses}
							onShowAll={handlers.onShowAll}
							onTimeChange={handlers.onTimeChange}
							requestsAgentIds={requestsAgentIds}
							highlightedRequestId={highlightedRequestId}
							originsByRequestId={origins.byRequestId}
							outreachCoachMark={outreachCoachMark}
						/>
						<RequestSection
							title="Next Week"
							requests={groupedRequests.nextWeek}
							isExpanded={expandedSections.nextWeek}
							onToggle={() => toggleSection("nextWeek")}
							onDelete={handlers.onDelete}
							onBroadcast={handlers.onBroadcast}
							onEditNeededNurses={handlers.onEditNeededNurses}
							onShowAll={handlers.onShowAll}
							onTimeChange={handlers.onTimeChange}
							requestsAgentIds={requestsAgentIds}
							highlightedRequestId={highlightedRequestId}
							originsByRequestId={origins.byRequestId}
							outreachCoachMark={outreachCoachMark}
						/>
						<RequestSection
							title="Future"
							requests={groupedRequests.future}
							isExpanded={expandedSections.future}
							onToggle={() => toggleSection("future")}
							onDelete={handlers.onDelete}
							onBroadcast={handlers.onBroadcast}
							onEditNeededNurses={handlers.onEditNeededNurses}
							onShowAll={handlers.onShowAll}
							onTimeChange={handlers.onTimeChange}
							requestsAgentIds={requestsAgentIds}
							highlightedRequestId={highlightedRequestId}
							originsByRequestId={origins.byRequestId}
							outreachCoachMark={outreachCoachMark}
						/>
					</>
				)}
			</Box>
			{dialogs}
		</Box>
	);
};
